import { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Instagram, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { GALLERY, GALLERY_FILTERS, CONTACT } from "../../data/site";
import { SectionHead } from "../SectionHead";
import { CtaButton } from "../Buttons";

const Lightbox = ({ items, index, setIndex }) => {
  const open = index !== null;
  const item = open ? items[index] : null;
  const go = useCallback((d) => setIndex((i) => (i + d + items.length) % items.length), [items.length, setIndex]);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open, go]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent data-testid="gallery-lightbox" className="max-w-[min(1100px,94vw)] border-0 bg-[color:var(--ngp-ink)] p-0 text-white [&>button]:hidden" data-lenis-prevent>
        {item && (
          <>
            <DialogTitle className="sr-only">{item.title}</DialogTitle>
            <DialogDescription className="sr-only">{item.alt}</DialogDescription>
            <img src={item.img} alt={item.alt} className="max-h-[78vh] w-full object-contain" />
            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <div><p className="eyebrow text-[color:var(--ngp-teal)]">{item.cat}</p><p className="font-display text-lg">{item.title}</p></div>
              <div className="flex gap-2">
                <button onClick={() => go(-1)} aria-label="Precedente" data-testid="lightbox-prev" className="grid h-11 w-11 place-items-center rounded-full border border-white/20 hover:bg-white hover:text-black"><ChevronLeft size={18} /></button>
                <button onClick={() => go(1)} aria-label="Successiva" data-testid="lightbox-next" className="grid h-11 w-11 place-items-center rounded-full border border-white/20 hover:bg-white hover:text-black"><ChevronRight size={18} /></button>
                <button onClick={() => setIndex(null)} aria-label="Chiudi" data-testid="lightbox-close" className="grid h-11 w-11 place-items-center rounded-full bg-[color:var(--ngp-teal)] text-black"><X size={18} /></button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = () => {
  const [filter, setFilter] = useState("Tutti");
  const [index, setIndex] = useState(null);
  const items = useMemo(() => (filter === "Tutti" ? GALLERY : GALLERY.filter((g) => g.cat === filter)), [filter]);
  return (
    <section id="progetti" data-testid="gallery-section" className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
      <SectionHead index="04" eyebrow="Progetti" lines={["Lavori che", "si vedono."]}
        aside={<CtaButton href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" variant="outline" testId="gallery-instagram-btn"><Instagram size={16} className="-ml-1 mr-1 inline" aria-hidden="true" />Seguici su Instagram</CtaButton>} />
      <div role="tablist" aria-label="Filtra progetti per categoria" className="mt-12 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none]">
        {GALLERY_FILTERS.map((f) => (
          <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${filter === f ? "border-[color:var(--ngp-ink)] bg-[color:var(--ngp-ink)] text-white" : "border-[color:var(--ngp-line)] bg-white hover:border-[color:var(--ngp-ink)]"}`}>
            {f}
          </button>
        ))}
      </div>
      <motion.div layout className="mt-8 columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
        <AnimatePresence initial={false}>
          {items.map((g, i) => (
            <motion.button layout key={g.img + g.cat} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.45 }}
              onClick={() => setIndex(i)} data-testid={`gallery-item-${i}`} aria-label={`Apri immagine: ${g.title}`}
              className={`group relative block w-full break-inside-avoid overflow-hidden rounded-[var(--ngp-radius)] bg-[color:var(--ngp-ink)] text-left ${g.h === "tall" ? "aspect-[4/5]" : "aspect-[4/3]"}`}>
              <img src={g.img} alt={g.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-105" />
              <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5 text-white">
                <span><span className="eyebrow block text-[color:var(--ngp-teal)]">{g.cat}</span><span className="font-display text-xl">{g.title}</span></span>
                <Maximize2 size={18} className="translate-y-2 opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-y-0 group-hover:opacity-100" aria-hidden="true" />
              </span>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>
      <Lightbox items={items} index={index} setIndex={setIndex} />
    </section>
  );
};
